import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentPetMedicalCheckUpReportLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {
    NatPutPetMedicalCheckUpReportApplyRequest,
    NatPutPetMedicalCheckUpReportApplyResponse,
} from '../repository/NatPutPetMedicalCheckUpReport'
import NatResidentPetMedicalCheckUpReportForm from './resident/NatResidentPetMedicalCheckUpReportForm'
import {FileInformation} from '../../../../components/applications/ResidentAttachmentFileArea'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<NatResidentPetMedicalCheckUpReportLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    petType: Nullable<string>
    petBreed: Nullable<string>
    dogLicenseFile: Nullable<FileInformation>
    rabiesFile: Nullable<FileInformation>
    medicalCheckUpReport: Nullable<FileInformation[]>
    isLoadCompleted: boolean
}

class NatResidentPetMedicalCheckUpReportInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            petType: null,
            petBreed: null,
            dogLicenseFile: null,
            rabiesFile: null,
            medicalCheckUpReport: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultPetType = applyInfo?.petType ?? null
        let defaultPetBreed = applyInfo?.petBreed ?? null
        let defaultDogLicense = applyInfo?.healthCenterDogLicense ?? null
        let defaultRabies = applyInfo?.rabiesPreventionInjectionCertification ?? null
        let defaultMedicalCheckUpReport = applyInfo?.medicalCheckUpReport ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const medicalCheckUpReportDetail =
                    await this.props.asyncCallFunction(applicationRepo.getPetMedicalCheckUpReportForResident.bind(
                            applicationRepo, condominiumCd, applyId),
                        (httpErrorOrNetworkError) => {
                            eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        },
                    )
                if (medicalCheckUpReportDetail?.details) {
                    defaultPetType = medicalCheckUpReportDetail.details.petType
                    defaultPetBreed = medicalCheckUpReportDetail.details.petBreed
                    defaultDogLicense = {
                        fileName: medicalCheckUpReportDetail.details.healthCenterDogLicense.fileName,
                        fileData: null,
                        fileNumber: medicalCheckUpReportDetail.details.healthCenterDogLicense.fileNumber,
                    }
                    defaultRabies = {
                        fileName: medicalCheckUpReportDetail.details.rabiesPreventionInjectionCertification.fileName,
                        fileData: null,
                        fileNumber:
                        medicalCheckUpReportDetail.details.rabiesPreventionInjectionCertification.fileNumber,
                    }
                    defaultMedicalCheckUpReport =
                        medicalCheckUpReportDetail.details.medicalCheckUpReport.map(attachment => {
                            return {
                                fileName: attachment.fileName,
                                fileData: null,
                                fileIndex: attachment.seq,
                                fileNumber: attachment.fileNumber,
                            }
                        })
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }
        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            petType: defaultPetType,
            petBreed: defaultPetBreed,
            dogLicenseFile: defaultDogLicense,
            rabiesFile: defaultRabies,
            medicalCheckUpReport: defaultMedicalCheckUpReport,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutPetMedicalCheckUpReportApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutPetMedicalCheckUpReportApplyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyPetMedicalCheckUpReport.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentPetMedicalCheckUpReportLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        petType: request.petType,
                        petBreed: request.petBreed,
                        healthCenterDogLicense: request.healthCenterDogLicense,
                        rabiesPreventionInjectionCertification: request.rabiesPreventionInjectionCertification,
                        medicalCheckUpReport: request.medicalCheckUpReport,
                    },
                    applyId: this.props.location.state.applyId,
                }
                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/petMedicalCheckUpReport/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/petMedicalCheckUpReport/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/petMedicalCheckUpReport/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    健康診断報告書
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentPetMedicalCheckUpReportForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentPetMedicalCheckUpReportInputPage)
